import { keyframes, styled as muiStyled } from "@mui/material/styles";
import { ListItemButton, Box, List, ListItemIcon, ListItemText } from "@mui/material";
import styled from "styled-components";

const wiggleAnimation = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-8deg); }
  75% { transform: rotate(8deg); }
`;

export const Container = styled(Box)`
  width: 100%;
  color: var(--dp-text);
  flex-shrink: 0;
  overflow: hidden;
  margin-bottom: 16px;
`;

export const ContainerInner = styled(Box)`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  text-align: center;
  gap: 16px;
  color: var(--dp-text);
  flex-shrink: 0;
  overflow-y: auto;
  border-radius: 16px;
  overflow-x: hidden;
  background-color: var(--dp-surface);
  border: 1px solid var(--dp-border);
  padding: 8px 14px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--dp-border-strong);
    border-radius: 3px;
  }
`;

export const ListStyled = muiStyled(List)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "0",
  flexWrap: "wrap",
  justifyContent: "center",
});

export const ListEndStyled = muiStyled(ListStyled)({
  justifyContent: "flex-end",
});

// `color` is a section name from theme/tokens.js (blue, green…); without it the item stays neutral
export const ListItemStyled = muiStyled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "$isSelected" && prop !== "color",
})(({ $isSelected, color }) => {
  const isSelected = $isSelected === "true";
  const sectionColor = color ? `var(--dp-section-${color})` : "var(--dp-text)";

  return {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    color: isSelected ? sectionColor : "var(--dp-text-soft)",
    width: "fit-content",
    flexGrow: 0,
    minHeight: 40,
    padding: "0 12px",
    backgroundColor: "transparent !important",
    textDecoration: "none",
    transition: "color 0.3s",
    position: "relative",
    borderRadius: 10,
    overflow: "hidden",
    zIndex: 1,

    "&::before": {
      content: '""',
      position: "absolute",
      top: isSelected ? "50%" : "200%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "100%",
      height: "100%",
      backgroundColor: `color-mix(in srgb, ${sectionColor} 14%, transparent)`,
      borderRadius: 10,
      transition: "top 0.3s ease",
      zIndex: -1,
    },

    "&:hover": {
      color: sectionColor,
    },

    "&:hover::before": {
      top: "50%",
    },

    "& svg": {
      color: color ? sectionColor : "currentColor",
      transition: "transform 0.3s ease",
    },

    "&:hover svg": {
      animation: `${wiggleAnimation} 0.4s ease`,
    },

    "& .MuiTypography-root": {
      fontWeight: isSelected ? 700 : 500,
    },
  };
});

export const ListItemIconStyled = muiStyled(ListItemIcon)({
  minWidth: "auto",
  color: "inherit",
});

export const Logo = styled.img`
  width: 100%;
  max-width: 200px;
  height: 40px;
  object-fit: contain;
`;

export const SuperAdminLogo = styled.div`
  text-align: center;
  color: var(--dp-text);
  font-weight: 700;
  padding: 8px;
`;

export const ListItemTextStyled = muiStyled(ListItemText)({
  width: "fit-content",
  flexGrow: 0,

  "& .MuiTypography-root": {
    fontSize: "0.8rem",
  },
});

export const ContainerLogo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  /* Agency logos are white: give them a dark backdrop in light mode */
  [data-theme="light"] & img {
    background-color: #17181c;
    padding: 6px 12px;
    border-radius: 10px;
    width: auto;
  }
`;

export const Separator = styled.span`
  width: 1px;
  height: 24px;
  background-color: var(--dp-border);
`;
